<template>
  <div class="custom-node destination-node">
    <Handle type="target" :position="Position.Left" class="node-handle" />
    
    <div class="node-header">
      <div class="node-icon">
        <Target :size="20" />
      </div>
      <div class="node-info">
        <span class="node-type">Destination</span>
        <span class="node-label">{{ data.label || 'Destination' }}</span>
      </div>
      <button @click="$emit('delete')" class="node-delete">
        <X :size="14" />
      </button>
    </div>
    
    <div class="node-body">
      <div v-if="data.tableName" class="node-detail">
        <span class="detail-label">Table:</span>
        <span class="detail-value">{{ data.tableName }}</span>
      </div>
      <div v-if="data.writeMode" class="node-detail">
        <span class="detail-label">Write Mode:</span>
        <span class="detail-value">{{ data.writeMode }}</span>
      </div>
      <div v-if="!data.tableName" class="node-placeholder">
        Click to configure
      </div>
    </div>
    
    <div v-if="data.tableName" class="node-status">
      <div class="status-dot ready"></div>
      <span>Ready</span>
    </div>
  </div>
</template>

<script setup>
import { Handle, Position } from '@vue-flow/core'
import { Target, X } from 'lucide-vue-next'

defineProps({
  data: {
    type: Object,
    required: true
  }
})

defineEmits(['delete'])
</script>

<style scoped>
.custom-node {
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  min-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-node:hover {
  border-color: var(--color-accent);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.destination-node {
  border-left: 4px solid var(--color-success);
}

.node-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.node-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--color-success), #059669);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.node-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.node-type {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  font-weight: 600;
}

.node-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-delete {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.node-delete:hover {
  background: rgba(220, 38, 38, 0.1);
  color: var(--color-error);
}

.node-body {
  padding: 1rem;
}

.node-detail {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.node-detail:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  font-weight: 600;
}

.detail-value {
  font-size: 13px;
  color: var(--color-text-primary);
  font-family: 'Monaco', 'Consolas', monospace;
}

.node-placeholder {
  text-align: center;
  padding: 1rem 0;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-style: italic;
}

.node-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(34, 197, 94, 0.05);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 12px;
  color: var(--color-success);
  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-success);
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
}

.node-handle {
  width: 12px;
  height: 12px;
  background: white;
  border: 2px solid var(--color-success);
  transition: all 0.2s;
}

.node-handle:hover {
  width: 16px;
  height: 16px;
  background: var(--color-success);
}
</style>